import type { components } from '../../api/generated/schema'
import type { MerchantBrief } from '../../api/merchant'
import { parseMoney, signedTransactionCents } from '../../api/parseMoney'

/**
 * The only place a `TransactionResponse` is read. Everything past this module
 * sees a `Txn`: money already parsed to integer cents and already signed for
 * display, display fields already resolved.
 *
 * **`amountCents` is signed here and nowhere else.** The wire is expense-positive
 * (`signedTransactionCents`' own docblock traces why), so a row's `amount` of
 * `"118.44"` is a $118.44 expense and becomes -11844. A second flip anywhere
 * downstream would invert every figure on the screen, which is why the
 * `transactionAmountSigning` sweep covers this file.
 */
export type TxnReviewStatus = 'pending' | 'reviewed' | 'dismissed' | 'not_required'
export type TxnCategorySource = 'provider' | 'rule' | 'ai' | 'user' | 'memory' | 'none'

export type Txn = {
  id: string
  accountId: string
  /** YYYY-MM-DD, a calendar date: never a Date, which would carry a timezone. */
  date: string
  /** `merchant_display_name ?? merchant_name ?? name`. */
  merchant: string
  /** The bank's own descriptor: `name`. */
  raw: string
  merchantId: string | null
  merchantBrief: MerchantBrief | null
  /** Signed for display: an expense is negative. */
  amountCents: number
  currency: string
  categoryId: string | null
  tags: string[]
  note: string | null
  /** `beneficial_owner_id`, the explicit override. Null means "account default". */
  ownerId: string | null
  /** The effective owner's `display_name ?? email`. */
  ownerName: string | null
  /** The effective owner's id (`effective_beneficial_owner.id`, a member's user id): identity, where `ownerName` is only a label. */
  effectiveOwnerId: string | null
  isPending: boolean
  reviewStatus: TxnReviewStatus
  categorySource: TxnCategorySource | null
  reviewedAt: string | null
  createdAt: string
  provider: string | null
  /**
   * `recurring_stream_id`: the recurring bill this transaction already settles,
   * or null. M7's payment picker hides a transaction that has one (a
   * transaction belongs to at most one live stream; linking it again 409s).
   */
  recurringStreamId: string | null
}

const REVIEW_STATUSES: readonly TxnReviewStatus[] = ['pending', 'reviewed', 'dismissed', 'not_required']
const CATEGORY_SOURCES: readonly TxnCategorySource[] = ['provider', 'rule', 'ai', 'user', 'memory', 'none']

/**
 * The wire types both as plain `string`. An unrecognised review status becomes
 * `not_required`: it is the one value that puts no row in the review queue, so
 * a value this client has not been taught can never pile rows into "Needs
 * review" or invite a bulk action on them. An unknown category source is `null`,
 * which the type admits for exactly this.
 */
function reviewStatusOf(s: string): TxnReviewStatus {
  return REVIEW_STATUSES.find((v) => v === s) ?? 'not_required'
}
function categorySourceOf(s: string): TxnCategorySource | null {
  return CATEGORY_SOURCES.find((v) => v === s) ?? null
}

/** M6's "Needs review": `review_status === 'pending'`. Bank-pending (`isPending`) is a different idea. Mirrors the server's `review_status=pending` filter that /transactions sends (filters.ts), so the Overview pill and the list agree. */
export const needsReview = (t: Pick<Txn, 'reviewStatus'>): boolean => t.reviewStatus === 'pending'

export function toTxn(t: components['schemas']['TransactionResponse']): Txn {
  const owner = t.effective_beneficial_owner
  return {
    id: t.id,
    accountId: t.account_id,
    date: t.transaction_date,
    merchant: t.merchant_display_name ?? t.merchant_name ?? t.name,
    raw: t.name,
    merchantId: t.merchant_id ?? null,
    merchantBrief: t.merchant ?? null,
    amountCents: signedTransactionCents(parseMoney(t.amount)),
    currency: t.iso_currency_code,
    categoryId: t.category_id ?? null,
    tags: t.tags,
    note: t.user_notes ?? null,
    ownerId: t.beneficial_owner_id ?? null,
    ownerName: owner?.display_name ?? owner?.email ?? null,
    effectiveOwnerId: owner?.id ?? null,
    isPending: t.is_pending,
    reviewStatus: reviewStatusOf(t.review_status),
    categorySource: categorySourceOf(t.category_source),
    reviewedAt: t.reviewed_at ?? null,
    createdAt: t.created_at,
    provider: t.provider ?? null,
    recurringStreamId: t.recurring_stream_id ?? null,
  }
}

/** One PATCH answer's offer: "Apply to `matchingCount` other transactions?". Held only for the prompt (the
 *  server has no endpoint to look a memory up). Null when the key is null or absent, or matches nothing. */
export type MemorySuggestion = { memoryId: string; matchingCount: number }

export function memorySuggestionOf(t: Pick<components['schemas']['TransactionResponse'], 'memory_suggestion'>): MemorySuggestion | null {
  const s = t.memory_suggestion
  if (s === null || s === undefined || !(s.matching_count >= 1)) return null
  return { memoryId: s.memory_id, matchingCount: s.matching_count }
}
